import { useState, useEffect } from "react";
import { Space, Button, Row, Col, Select, Form, notification, Typography, Input } from "antd";
import { LeftCircleOutlined } from "@ant-design/icons";
import {
  useLazyGetBookingByIdQuery,
  useUpdateBookingMutation
} from "../../../redux/services/bookingAPI";
import { localizeDateTime } from "../../../utils";
import { useGetPartnersQuery } from "../../../redux/services/partnerApi";
import { useLazyGetBrandModelsQuery } from "../../../redux/services/brandModelApi";
import ErrorDetails from "../../../components/ErrorDetails";
import { useNavigate, useParams } from "react-router-dom";
import {
  BrandModelSelect,
  BrandSelect,
  CheckBoxInput,
  CitySelect,
  CityManagerSelect,
  InputElement,
  ZoneSelect,
  PincodeInputElement,
  StateSelect,
  CustomerCitySelect
} from "../common/FormsElements";
import { useAppSelector } from "../../../redux/hooks";
import { yesNoOptions, yesNoNaOptions } from "../../../constants/helperConstants";

interface BookingFormValues {
  charger_software_updated?: boolean | "NA" | null;
  [key: string]: any;
}

const BookingForm = (props: any) => {
  const navigate = useNavigate();
  const { Option } = Select;
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();

  const [loading, setLoading] = useState(false);

  const params = useParams();

  const [update] = useUpdateBookingMutation();

  const [triggerGetBrandModels, { data: brandModelsData }] = useLazyGetBrandModelsQuery();
  const brandModels = brandModelsData?.brand_models || [];

  const onUpdateBooking = async (values: BookingFormValues, setLoading: any, form: any, id: any) => {
    try {
      setLoading(true);
      await update({ booking: { id, ...values } }).unwrap();
      api.success({
        message: "Update booking successful",
        placement: "top"
      });
      setLoading(false);
      form.resetFields();
      // setOpen(false);
      // refetch();
      if (window.history.length > 1) {
        history.back();
      } else {
        navigate("/bookings");
      }
    } catch (error: any) {
      api.info({
        message: "Update booking failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onGoBack = () => {
    navigate(-1);
  };

  const [locationId, setLocationId] = useState(null);
  const [getBookingById, { data }] = useLazyGetBookingByIdQuery();
  const booking = data?.booking;

  useEffect(() => {
    if (booking?.city?.id) {
      setLocationId(booking?.city?.id);
    }
  }, [booking]);

  const { data: partnerData, refetch: refetchPartners } = useGetPartnersQuery(
    {
      all_pages: true,
      location_id: locationId
    },
    { skip: !locationId }
  );

  const [brandId, setBrandId] = useState(booking?.brand?.id);

  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const isSuperUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user"].includes(role.role_type)
  );

  const isEditUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis", "super_user"].includes(role.role_type)
  );

  const isFinanceController = user?.roles?.some((role: { role_type: string }) =>
    ["finance_controller"].includes(role.role_type)
  );

  const isMisUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis"].includes(role.role_type)
  );

  const isCityManager = user?.roles?.some((role: { role_type: string }) =>
    ["city_manager"].includes(role.role_type)
  );

  const role_types = user.roles.map((role: any) => role.role_type);
  const isManager = !role_types
    .map((role_type: any) => ["city_manager", "zonal_manager"].includes(role_type))
    .includes(false);

  const todayDate = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (data) {
      const fieldsValue = { ...data.booking };
      fieldsValue.assigned_city_manager_id = booking?.assigned_city_manager?.id;
      fieldsValue.brand_id = booking?.brand?.id;
      fieldsValue.brand_name = booking?.brand?.name;
      fieldsValue.brand_model_id = booking?.brand_model?.id;
      fieldsValue.brand_booking_status = booking?.brand_booking_status;
      fieldsValue.zone_id = booking?.zone?.id;
      fieldsValue.city_id = booking?.city?.id;
      fieldsValue.state_id = booking?.state?.id;
      form.setFieldsValue(fieldsValue);
    }
  }, [data]);

  useEffect(() => {
    if (params?.bookingId) {
      getBookingById(params.bookingId);
    }
  }, [params?.bookingId]);

  const onFinish = (values: any) => {
    onUpdateBooking(values, setLoading, form, data.booking.id);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const handleBrandSelect = (value: string) => {
    triggerGetBrandModels({ all_pages: true, brand_id: value });
    form.setFieldsValue({ brand_id: value });
    form.setFieldsValue({ brand_model_id: null });
    setBrandId(value);
  };

  useEffect(() => {
    const brandId = booking?.brand?.id;
    if (brandId) {
      triggerGetBrandModels({ all_pages: true, brand_id: brandId });
    }
  }, [booking]);

  return (
    <div style={{ paddingBottom: 50 }}>
      <Row style={{ marginBottom: 20 }}>
        <Col
          sm={24}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <h2>Update Booking</h2>
          <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
            Back
          </Button>
        </Col>
      </Row>

      {contextHolder}

      <Form
        name="booking_form"
        form={form}
        initialValues={{ remember: true }}
        labelCol={{ span: 9 }}
        wrapperCol={{ span: 11 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        {!isManager && (
          <>
            <Typography style={{ fontWeight: 500 }}>Customer Details</Typography>
            <Row gutter={16}>
              <Col span={12}>
                <InputElement
                  label="Customer Name"
                  name="customer_name"
                  rules={[{ required: true, message: "Please Enter Customer Name" }]}
                />
                <InputElement label="Email" name="email" />
                <InputElement
                  label="Customer GST"
                  name="gst"
                  disabled={!isEditUser && booking?.gst}
                  rules={[
                    { min: 15, message: "GST Number must be minimum 15 digit." },
                    { max: 15, message: "GST Number be maximum 15 digit." }
                  ]}
                />
              </Col>
              <Col span={12}>
                <InputElement
                  label="Contact Number"
                  name="contact_number"
                  rules={[
                    {
                      required: true,
                      min: 10,
                      message: "Contact Number must be minimum 10 digit."
                    },
                    { max: 10, message: "Contact Number be maximum 10 digit." },
                    { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
                  ]}
                />

                <InputElement
                  label="Alt Contact Number"
                  name="alt_contact_number"
                  rules={[
                    { min: 10, message: "Contact Number must be minimum 10 digit." },
                    { max: 10, message: "Contact Number be maximum 10 digit." },
                    { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
                  ]}
                />
                <InputElement
                  label="Dealer GST"
                  name="dealer_gst"
                  disabled={!isEditUser && booking?.dealer_gst}
                  rules={[
                    { min: 15, message: "GST Number must be minimum 15 digit." },
                    { max: 15, message: "GST Number be maximum 15 digit." }
                  ]}
                />
              </Col>
            </Row>
            <Typography style={{ fontWeight: 500 }}>Vehicle Details</Typography>
            <Row gutter={16}>
              <Col span={12}>
                <InputElement label="Dealer" name="dealer_name" />
                <BrandSelect onSelect={handleBrandSelect} />
                <InputElement label="Model Variant" name="model_variant" />
                <InputElement label="Model Colour" name="model_colour" />
                <InputElement label="Model Int Color" name="model_int_color" />
              </Col>
              <Col span={12}>
                <InputElement label="Dealer Location" name="dealer_location" />
                <InputElement label="Dealer Email ID" name="dealer_email" />
                <BrandModelSelect brandModels={brandModels} />
                <InputElement label="Model Description" name="model_description" />
                <InputElement label="Model Ext Color" name="model_ext_color" />
              </Col>
            </Row>
            <Typography style={{ fontWeight: 500 }}>Address and Schedule</Typography>
            <Row gutter={16}>
              <Col span={12}>
                <InputElement
                  label="Booking Address"
                  name="booking_address"
                  rules={[{ required: true, message: "Please Enter Booking Address" }]}
                />
                <PincodeInputElement />
                <InputElement label="Address Line2" name="address_line2" />
                <InputElement label="Latitude" name="latitude" />
                <ZoneSelect disabled={!isEditUser} />
                <Form.Item label="Outstation for installer ?" name="is_outstation">
                  <Select
                    placeholder="Select Outstation"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>

                <Form.Item label="Outstation for Client ?" name="is_outstation_for_client">
                  <Select
                    placeholder="Select Outstation"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
                <InputElement label="VIN" name="vin" disabled={!isEditUser && booking?.vin} />
              </Col>
              <Col span={12}>
                <InputElement label="Address Line1" name="address_line1" />
                <InputElement label="Locality" name="locality" />
                <InputElement label="Longitude" name="longitude" />
                <CustomerCitySelect />
                <CitySelect />
                <StateSelect required />
                <InputElement
                  label="Brand Booking Code"
                  name="brand_booking_code"
                  disabled={!isSuperUser}
                />
                <InputElement
                  label="Brand Booking Status"
                  name="brand_booking_status"
                  disabled={!isSuperUser}
                />
                <Form.Item label="Expected Delivery Date" name="expected_delivery_date">
                  <Input type="date" />
                </Form.Item>
                <Form.Item label="PI Survey Send Date" name="pi_survey_send_date">
                  <Input type="date" disabled={!(isFinanceController || isEditUser)} />
                </Form.Item>
                <Form.Item label="PI Installation Send Date" name="pi_installation_send_date">
                  <Input type="date" disabled={!(isFinanceController || isEditUser)} />
                </Form.Item>
              </Col>
            </Row>
            <Typography style={{ fontWeight: 500 }}>Assignment</Typography>
            <Row gutter={16}>
              <Col span={12}>
                <InputElement label="Company Name" name="company_name" />
                <CheckBoxInput
                  label="Customer Ok With Recording"
                  name="customer_ok_with_recording"
                />
                <CityManagerSelect />
                <InputElement
                  label="Charger Type"
                  name="obc"
                  disabled={!(isSuperUser || isMisUser)}
                />
                <Form.Item label="Charger Issued Date" name="charger_issued_date">
                  <Input type="date" disabled={!isEditUser} />
                </Form.Item>
                <Form.Item
                  label="Customer Proposed Date Survey"
                  name="customer_proposed_survey_date"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <Form.Item
                  label="Updation Proposed Date Survey"
                  name="customer_proposed_survey_date_updated_at"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <Form.Item label="BHE" name="bhe">
                  <Select
                    placeholder="Select BHE"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={!isSuperUser}
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
                <InputElement label="CEE Name" name="cee_name" />
                <Form.Item label="CEE Contact Date" name="cee_contact_date">
                  <Input type="date" />
                </Form.Item>
                <Form.Item
                  label="Has the charger software been updated?"
                  name="charger_software_updated"
                >
                  <Select
                    placeholder="Select Option"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoNaOptions}
                  ></Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <InputElement label="Site POC Name" name="site_poc_name" />
                <InputElement label="Site POC Contact" name="site_poc_contact" />
                <InputElement label="Remarks" name="remarks" />
                {isSuperUser && (
                  <Form.Item label="ECB Recieve Date" name="ecb_receive_date">
                    <Input type="date" />
                  </Form.Item>
                )}
                <Form.Item label="BHE Date" name="bhe_date">
                  <Input disabled={!isSuperUser} type="date" min={todayDate} />
                </Form.Item>
                <Form.Item
                  label="Customer Proposed Date Install"
                  name="customer_proposed_install_date"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <Form.Item
                  label="Updation Proposed Install Date"
                  name="customer_proposed_install_date_updated_at"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <InputElement
                  disabled={!isEditUser}
                  label="City Manager Email ID"
                  name="city_manager_email"
                />
                <InputElement
                  disabled={!isEditUser}
                  label="Zonal Manager Email ID"
                  name="zonal_manager_email"
                />
                <InputElement label="Commission Number" disabled={booking?.commission_number && !isEditUser} name="commission_number" />
                <Form.Item
                  label="Charger Software Updated By"
                  name="software_install_partner_id"
                >
                  <Select
                    placeholder="Please Select Option"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option: any) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={(partnerData?.partners || []).map((record: any) => ({
                      value: record.id,
                      label: record.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {isManager && !isSuperUser && (
          <>
            <Typography style={{ fontWeight: 500 }}>Assignment</Typography>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="BHE" name="bhe">
                  <Select
                    placeholder="Select BHE"
                    allowClear
                    showSearch
                    disabled={booking?.bhe}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
                <InputElement
                  disabled={!isEditUser}
                  label="City Manager Email ID"
                  name="city_manager_email"
                />
                <InputElement
                  disabled={!isEditUser}
                  label="Zonal Manager Email ID"
                  name="zonal_manager_email"
                />
                <InputElement
                  label="Customer GST"
                  name="gst"
                  disabled={booking?.gst}
                  rules={[
                    { min: 15, message: "GST Number must be minimum 15 digit." },
                    { max: 15, message: "GST Number be maximum 15 digit." }
                  ]}
                />
                <Form.Item
                  label="Customer Proposed Date Survey"
                  name="customer_proposed_survey_date"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <Form.Item
                  label="Updation Proposed Date Survey"
                  name="customer_proposed_survey_date_updated_at"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <InputElement label="VIN" name="vin" disabled={!isEditUser && booking?.vin} />
                <Form.Item
                  label="Has the charger software been updated?"
                  name="charger_software_updated"
                >
                  <Select
                    placeholder="Select Option"
                    allowClear
                    showSearch
                    disabled={!isEditUser && isCityManager}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoNaOptions}
                  ></Select>
                </Form.Item>
                <Form.Item label="PI Installation Send Date" name="pi_installation_send_date">
                  <Input type="datetime-local" disabled={!(isFinanceController || isEditUser)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="BHE Date" name="bhe_date">
                  <Input disabled={booking?.bhe_date} type="date" min={todayDate} />
                </Form.Item>
                <InputElement disabled={!isEditUser} label="Dealer Email ID" name="dealer_email" />
                <Form.Item label="Charger Issued Date" name="charger_issued_date">
                  <Input type="date" />
                </Form.Item>
                <InputElement
                  label="Dealer GST"
                  name="dealer_gst"
                  disabled={booking?.dealer_gst}
                  rules={[
                    { min: 15, message: "GST Number must be minimum 15 digit." },
                    { max: 15, message: "GST Number be maximum 15 digit." }
                  ]}
                />
                <Form.Item
                  label="Customer Proposed Date Install"
                  name="customer_proposed_install_date"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <Form.Item
                  label="Updation Proposed Install Date"
                  name="customer_proposed_install_date_updated_at"
                >
                  <Input
                    type="date"
                    disabled={!isEditUser}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Item>
                <InputElement label="Commission Number" disabled={booking?.commission_number && !isEditUser} name="commission_number" />
                <Form.Item
                  label="Charger Software Updated By"
                  name="software_install_partner_id"
                >
                  <Select
                    placeholder="Please Select Option"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    disabled={!isEditUser && booking?.software_install_partner_id}
                    filterOption={(input, option: any) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={(partnerData?.partners || []).map((record: any) => ({
                      value: record.id,
                      label: record.name,
                    }))}
                  />
                </Form.Item>
                <Form.Item label="PI Survey Send Date" name="pi_survey_send_date">
                  <Input type="datetime-local" disabled={!(isFinanceController || isEditUser)} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Form.Item wrapperCol={{ offset: 19, span: 24 }}>
          <Row>
            <Col>
              <Space size={"middle"}>
                <Button
                  type="default"
                  onClick={() => {
                    form.resetFields();
                    onGoBack();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Update
                </Button>
              </Space>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </div>
  );
};

export default BookingForm;
